
import { defineComponent, PropType } from 'vue'
import UpgradeSummaryMixin from '~/mixins/dependencies/upgradeSummaryMixin'
import { VulnerabilityRemediationFixPathUpdate } from '~/types/types'

export default defineComponent({
  name: 'PackageUpgradeAIOverview',
  mixins: [UpgradeSummaryMixin],
  props: {
    update: {
      type: Object as PropType<VulnerabilityRemediationFixPathUpdate> | null,
      required: true
    }
  },
  async fetch() {
    try {
      await this.fetchPackageUpgradeSummary(this.update)
    } catch (error: any) {
      this.$logErrorAndToast(
        error as Error,
        'Failed to fetch upgrade summary. Please contact support.'
      )
    }
  },
  data() {
    return {
      isExpanded: false,
      timeoutRef: null as ReturnType<typeof setTimeout> | null,
      retryCount: 0,
      generating: false
    }
  },
  beforeDestroy() {
    if (process.client && this.timeoutRef) {
      clearTimeout(this.timeoutRef)
    }
  },
  methods: {
    /**
     * Starts polling for package upgrade summary
     * @param update - Object containing package information and version details
     */
    async startPolling(update: VulnerabilityRemediationFixPathUpdate) {
      const MAX_RETRIES = 11
      if (this.retryCount >= MAX_RETRIES) {
        if (this.timeoutRef) {
          clearTimeout(this.timeoutRef)
          this.timeoutRef = null
        }
        return
      }

      await this.fetchPackageUpgradeSummary(update, true)

      if (this.summary) {
        this.generating = false
        if (process.client && this.timeoutRef) {
          clearTimeout(this.timeoutRef)
        }
        this.timeoutRef = null
        this.retryCount = 0
      } else {
        this.retryCount++
        this.timeoutRef = setTimeout(() => {
          this.startPolling(update)
        }, 1000 * Math.pow(2, this.retryCount))
      }
    },
    async toggleExpanded() {
      this.isExpanded = !this.isExpanded
      if (this.isExpanded) {
        if (this.generating || this.summary) return // fetching or already fetched
        this.generating = true
        try {
          const res = await this.doGeneratePackageUpgradeSummary(this.update)
          if (res.data?.generatePackageUpgradeSummary?.isGenerated) {
            this.summary = res.data.generatePackageUpgradeSummary.summary ?? null
            this.generating = false
          } else {
            await this.startPolling(this.update)
          }
        } catch (error: any) {
          this.$logErrorAndToast(
            error as Error,
            'Failed to generate upgrade summary. Please contact support.'
          )
          this.generating = false
          if (process.client && this.timeoutRef) {
            clearTimeout(this.timeoutRef)
            this.timeoutRef = null
          }
        }
      } else {
        if (process.client && this.timeoutRef) {
          clearTimeout(this.timeoutRef)
          this.timeoutRef = null
        }
        this.$emit('close')
        this.retryCount = 0
      }
    }
  }
})
